<script setup lang="ts">
import { computed, ref } from 'vue';
import { assetUrl } from '@/data/directus';
import { campusName } from '@/data/campuses';
import { categoryByKey } from '@/data/categories';
import { maskIcon } from '@/data/css';
import accessibleIcon from '@/assets/ui/icons/accessible-icon-brands.svg';
import type { MapLocation } from '@/data/types';
import PanelHeading from './PanelHeading.vue';

const props = defineProps<{ location: MapLocation; shareUrl: string }>();
const emit = defineEmits<{ back: []; directions: [] }>();

const category = computed(() => categoryByKey(props.location.category));
const imageFailed = ref(false);
const copyStatus = ref('');

async function copyLink() {
  try {
    await navigator.clipboard.writeText(props.shareUrl);
    copyStatus.value = 'Link copied';
  } catch {
    copyStatus.value = 'Copy failed';
  }
  setTimeout(() => (copyStatus.value = ''), 2500);
}
</script>

<template>
  <article class="details">
    <PanelHeading
      :title="location.title"
      :back-label="`Back to ${category?.label ?? 'list'}`"
      @back="emit('back')"
    />

    <p class="details__meta">
      <span
        v-if="category"
        class="mask-icon"
        :style="maskIcon(category.iconUrl)"
        aria-hidden="true"
      />
      {{ category?.label ?? location.category }} · {{ campusName(location.campus) }}
    </p>

    <img
      v-if="location.imageId && !imageFailed"
      :key="location.imageId"
      class="details__image"
      :src="assetUrl(location.imageId, 640)"
      :srcset="`${assetUrl(location.imageId, 360)} 360w, ${assetUrl(location.imageId, 720)} 720w`"
      sizes="(min-width: 768px) 330px, 100vw"
      alt=""
      loading="lazy"
      @error="imageFailed = true"
    />

    <p v-if="location.address" class="details__address">{{ location.address }}</p>

    <p v-if="location.description" class="details__desc">{{ location.description }}</p>

    <p v-if="location.url" class="details__more">
      <a :href="location.url" target="_blank" rel="noopener">
        More info<span class="visually-hidden"> about {{ location.title }} (new tab)</span>
      </a>
    </p>

    <div v-if="location.accessibilityNotes || location.accessibleEntrance" class="details__acc">
      <span class="mask-icon" :style="maskIcon(accessibleIcon)" aria-hidden="true" />
      <div>
        <strong>Accessibility</strong>
        <p v-if="location.accessibilityNotes">{{ location.accessibilityNotes }}</p>
        <p v-if="location.accessibleEntrance">Directions can take you to the step-free entrance.</p>
      </div>
    </div>

    <div class="details__actions">
      <button type="button" class="btn btn--primary" @click="emit('directions')">
        <svg viewBox="0 0 512 512" width="14" height="14" aria-hidden="true">
          <path
            fill="currentColor"
            d="M502.6 233.3 278.7 9.4a32 32 0 0 0-45.3 0L9.4 233.3a32 32 0 0 0 0 45.3l223.9 223.9a32 32 0 0 0 45.3 0l223.9-223.9a32 32 0 0 0 0-45.3zM384 264l-88 72c-5.2 4.3-12 .6-12-6.2V288h-88v64a8 8 0 0 1-8 8h-32a8 8 0 0 1-8-8v-80c0-17.7 14.3-32 32-32h104v-41.8c0-6.8 6.8-10.5 12-6.2l88 72a8 8 0 0 1 0 12.4z"
          />
        </svg>
        Directions
      </button>
      <button type="button" class="btn" @click="copyLink">Copy link</button>
    </div>
    <p class="details__status" role="status">{{ copyStatus }}</p>
  </article>
</template>

<style scoped>
.details__meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 12px;
  color: var(--text-muted);
  font-size: 14px;
}

.details__meta .mask-icon {
  width: 16px;
  height: 16px;
  color: var(--interactive-blue);
}

.details__image {
  display: block;
  width: 100%;
  aspect-ratio: 3 / 2;
  object-fit: cover;
  border-radius: var(--radius);
  background: var(--surface-alt);
}

.details__desc {
  margin: 12px 0 0;
  line-height: 1.5;
  font-size: 15px;
}

.details__address {
  margin: 12px 0 0;
  font-size: 14px;
  color: var(--text-muted);
}

.details__more {
  margin: 10px 0 0;
  font-size: 14px;
}

.details__more a {
  color: var(--interactive-blue);
  font-weight: 700;
}

.details__acc {
  display: flex;
  gap: 10px;
  margin-top: 14px;
  padding: 10px 12px;
  border-radius: var(--radius);
  background: var(--surface-alt);
  font-size: 14px;
}

.details__acc .mask-icon {
  margin-top: 2px;
  color: var(--interactive-blue);
}

.details__acc p {
  margin: 4px 0 0;
}

.details__actions {
  display: flex;
  gap: 8px;
  margin-top: 16px;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 16px;
  border: 1px solid var(--interactive-blue);
  border-radius: var(--radius);
  background: var(--surface);
  color: var(--interactive-blue);
  font-weight: 700;
  font-size: 14px;
  text-decoration: none;
}

.btn:hover {
  background: var(--surface-alt);
}

.btn--primary {
  flex: 1;
  gap: 8px;
  background: var(--interactive-blue);
  color: #fff;
}

.btn--primary:hover {
  background: var(--interactive-blue-hover);
}

.details__status {
  min-height: 1.2em;
  margin: 6px 0 0;
  font-size: 13px;
  color: var(--text-muted);
}
</style>
